import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';

const PAGE_STATES = {
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
    RESUBSCRIBED: 'resubscribed',
};

const Unsubscribe = () => {
    const location = useLocation();
    const [pageState, setPageState] = useState(PAGE_STATES.LOADING);
    const [message, setMessage] = useState('');
    const [isResubscribing, setIsResubscribing] = useState(false);

    const { email, token } = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            email: params.get('email') || '',
            token: params.get('token') || '',
        };
    }, [location.search]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!email || !token) {
                setPageState(PAGE_STATES.ERROR);
                setMessage('Missing unsubscribe link details. Please use the link from your email.');
                return;
            }

            try {
                const response = await axios.get('/api/unsubscribe', {
                    params: { email, token },
                });

                if (cancelled) return;
                setPageState(PAGE_STATES.SUCCESS);
                setMessage(response.data?.message || 'You have been unsubscribed.');
            } catch (error) {
                if (cancelled) return;
                setPageState(PAGE_STATES.ERROR);
                setMessage(
                    error?.response?.data?.message ||
                    'We could not process your unsubscribe request. Please try again later.'
                );
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [email, token]);

    const handleResubscribe = async () => {
        setIsResubscribing(true);

        try {
            const response = await axios.post('/api/resubscribe', { email, token });
            setPageState(PAGE_STATES.RESUBSCRIBED);
            setMessage(response.data?.message || 'You have been resubscribed.');
        } catch (error) {
            setPageState(PAGE_STATES.ERROR);
            setMessage(
                error?.response?.data?.message ||
                'We could not process your resubscribe request. Please try again later.'
            );
        } finally {
            setIsResubscribing(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Box textAlign="center" mt={4}>
                {pageState === PAGE_STATES.LOADING && (
                    <>
                        <Typography variant="h4" gutterBottom>
                            Processing request...
                        </Typography>
                        <Typography variant="body1">
                            Please wait while we update your email preferences.
                        </Typography>
                    </>
                )}

                {(pageState === PAGE_STATES.SUCCESS || pageState === PAGE_STATES.RESUBSCRIBED) && (
                    <>
                        <Typography variant="h4" gutterBottom>
                            {pageState === PAGE_STATES.SUCCESS ? 'Preferences Updated' : 'Subscription Restored'}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {message}
                        </Typography>
                        {pageState === PAGE_STATES.SUCCESS && (
                            <Box mt={3}>
                                <Button
                                    variant="contained"
                                    onClick={handleResubscribe}
                                    disabled={isResubscribing}
                                >
                                    {isResubscribing ? 'Resubscribing...' : 'Resubscribe'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}

                {pageState === PAGE_STATES.ERROR && (
                    <>
                        <Typography variant="h4" gutterBottom>
                            Unable to Update Preferences
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {message}
                        </Typography>
                    </>
                )}

                <Box mt={5}>
                    <Button variant="outlined" component={Link} to="/">
                        Back to Home
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default Unsubscribe;
